import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Archive, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock,
  AlertTriangle
} from 'lucide-react';

interface ArchiveCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  caseTitle: string;
  onSuccess: () => void;
}

export const ArchiveCaseModal = ({ 
  isOpen, 
  onClose, 
  caseId, 
  caseTitle, 
  onSuccess 
}: ArchiveCaseModalProps) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [archiveData, setArchiveData] = useState({
    final_outcome: '',
    archive_reason: '',
    archive_type: 'finalizado'
  });

  const handleArchive = async () => {
    if (!archiveData.final_outcome.trim()) {
      toast({ 
        title: "Erro", 
        description: "Descreva o desfecho final do processo", 
        variant: "destructive" 
      });
      return;
    }

    setIsLoading(true);
    try {
      const now = new Date().toISOString();
      
      const { error } = await supabase
        .from('cases')
        .update({
          archived: true,
          archive_date: now,
          final_outcome: archiveData.final_outcome,
          archive_reason: archiveData.archive_reason || null,
          status: 'Finalizado',
          updated_at: now
        })
        .eq('id', caseId);

      if (error) throw error;

      toast({ 
        title: "Processo Arquivado!", 
        description: "O processo foi movido para o arquivo com sucesso." 
      });
      
      onSuccess();
      onClose();
      
      // Limpar formulário
      setArchiveData({
        final_outcome: '',
        archive_reason: '',
        archive_type: 'finalizado'
      });
      
    } catch (error: any) {
      console.error('Erro ao arquivar processo:', error);
      toast({ 
        title: "Erro", 
        description: "Erro ao arquivar o processo", 
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white shadow-2xl border border-gray-200">
        <DialogHeader className="bg-gradient-to-r from-orange-50 to-red-50 p-6 -m-6 mb-6 border-b border-orange-200">
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-800 to-red-800 drop-shadow-lg">
            <Archive className="w-6 h-6" />
            Arquivar Processo
          </DialogTitle>
          <DialogDescription className="text-orange-700/90 mt-3 text-lg">
            Marque este processo como finalizado e mova-o para o arquivo. 
            Esta ação não pode ser desfeita facilmente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações do processo */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Processo a ser arquivado:</h4>
            <p className="text-sm text-muted-foreground">{caseTitle}</p>
          </div>

          {/* Tipo de arquivamento */}
          <div className="space-y-2">
            <Label htmlFor="archive_type">Tipo de Arquivamento</Label>
            <Select
              value={archiveData.archive_type}
              onValueChange={(value) => setArchiveData(prev => ({ ...prev, archive_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="finalizado">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Finalizado
                  </div>
                </SelectItem>
                <SelectItem value="suspenso">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    Suspenso
                  </div>
                </SelectItem>
                <SelectItem value="cancelado">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-600" />
                    Cancelado
                  </div>
                </SelectItem>
                <SelectItem value="outro">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                    Outro
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Desfecho final */}
          <div className="space-y-2">
            <Label htmlFor="final_outcome">
              Desfecho Final *
              <span className="text-sm text-muted-foreground ml-2">
                (Descreva como o processo foi resolvido)
              </span>
            </Label>
            <Textarea
              id="final_outcome"
              value={archiveData.final_outcome}
              onChange={(e) => setArchiveData(prev => ({ ...prev, final_outcome: e.target.value }))}
              placeholder="Ex: Processo julgado procedente. Cliente recebeu indenização de R$ 50.000,00. Sentença transitou em julgado."
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Motivo do arquivamento (opcional) */}
          <div className="space-y-2">
            <Label htmlFor="archive_reason">
              Motivo do Arquivamento
              <span className="text-sm text-muted-foreground ml-2">(Opcional)</span>
            </Label>
            <Textarea
              id="archive_reason"
              value={archiveData.archive_reason}
              onChange={(e) => setArchiveData(prev => ({ ...prev, archive_reason: e.target.value }))}
              placeholder="Ex: Processo finalizado após acordo extrajudicial. Cliente satisfeito com o resultado."
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Aviso importante */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-orange-800 mb-1">Atenção!</p>
                <p className="text-orange-700">
                  Após arquivar, o processo será movido para a pasta "Arquivo" e não aparecerá mais na lista principal. 
                  Você ainda poderá acessá-lo através da seção de arquivo.
                </p>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button 
              onClick={handleArchive} 
              disabled={isLoading || !archiveData.final_outcome.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isLoading ? (
                <>
                  <Archive className="w-4 h-4 mr-2 animate-pulse" />
                  Arquivando...
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  Arquivar Processo
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 